import { CurrencyPipe, DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { InvoiceStateByAPI } from '@common/enums';
import { InvoiceOData } from '@common/models';
import { SortOrderByPipe } from '@common/pipes';
import { InvoicesService } from '@common/services';
import { getUniqueListByProp, PaymentsTag, PaymentsTagsMap } from '@common/services/smart-search';
import SmartSearchTemplatesBase from '@common/services/smart-search/smart-search-template-base';
import cloneDeep from 'lodash/cloneDeep';
import { ModelTemplate } from 'mbs-ui-kit';
import { asapScheduler, Observable, scheduled } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class SmartSearchModelTemplatePaymentsService extends SmartSearchTemplatesBase {
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly datePipe = inject(DatePipe);
  private readonly paymentsService = inject(InvoicesService);
  private readonly sortOrderByPipe = inject<SortOrderByPipe<InvoiceOData>>(SortOrderByPipe);
  private readonly paymentsService$ = this.paymentsService.getInvoices();

  readonly #amount = PaymentsTagsMap.get(PaymentsTag.Amount);
  readonly #date = PaymentsTagsMap.get(PaymentsTag.Date);
  readonly #expireDate = PaymentsTagsMap.get(PaymentsTag.ExpireDate);
  readonly #invoiceId = PaymentsTagsMap.get(PaymentsTag.InvoiceId);
  readonly #price = PaymentsTagsMap.get(PaymentsTag.Price);
  readonly #quantity = PaymentsTagsMap.get(PaymentsTag.Quantity);
  readonly #state = PaymentsTagsMap.get(PaymentsTag.State);

  private keyCache = 'payments-name_';

  public readonly StateTag: ModelTemplate<InvoiceOData> = {
    tag: this.#state.tag,
    items: (state): Observable<InvoiceOData[]> => {
      const term = state.leftCaretValue;

      this.paymentsService.filter = term || '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<InvoiceOData[]>;
      // }

      return scheduled(
        this.paymentsService$.pipe(
          map((res) => res.data),
          map((invoices) => {
            const filtered = invoices.filter((invoice) => invoice.State != null && invoice.State.includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#state.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, this.#state.prop);
          }),
          tap<InvoiceOData[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ State }): string => InvoiceStateByAPI[State]?.displayName || '',
    addGroupBrackets: true
  };

  public readonly QuantityTag: ModelTemplate<InvoiceOData> = {
    tag: this.#quantity.tag,
    items: (state): Observable<InvoiceOData[]> => {
      const term = state.leftCaretValue;

      this.paymentsService.filter = term || '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<InvoiceOData[]>;
      // }

      return scheduled(
        this.paymentsService$.pipe(
          map((res) => res.data),
          map((invoices) => {
            const filtered = invoices.filter((invoice) => invoice.Quantity != null && String(invoice.Quantity).includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#quantity.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, 'Quantity', 'number');
          }),
          tap<InvoiceOData[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ Quantity }): string => `${Quantity}`,
    addGroupBrackets: true
  };

  public readonly PriceTag: ModelTemplate<InvoiceOData> = {
    tag: this.#price.tag,
    items: (state): Observable<InvoiceOData[]> => {
      const term = state.leftCaretValue;

      this.paymentsService.filter = term || '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<InvoiceOData[]>;
      // }

      return scheduled(
        this.paymentsService$.pipe(
          map((res) => res.data),
          map((invoices) => {
            const filtered = invoices.filter((invoice) => invoice.Price != null && String(invoice.Price).includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#price.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, 'Price', 'number');
          }),
          tap<InvoiceOData[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ Price }): string => this.currencyPipe.transform(Price / 100, 'USD', 'symbol'),
    addGroupBrackets: true
  };

  public readonly AmountTag: ModelTemplate<InvoiceOData> = {
    tag: this.#amount.tag,
    items: (state): Observable<InvoiceOData[]> => {
      const term = state.leftCaretValue;

      this.paymentsService.filter = term || '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<InvoiceOData[]>;
      // }

      return scheduled(
        this.paymentsService$.pipe(
          map((res) => res.data),
          map((invoices) => {
            const filtered = invoices.filter((invoice) => invoice.Amount != null && String(invoice.Amount).includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#amount.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, 'Amount', 'number');
          }),
          tap<InvoiceOData[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ Amount }): string => this.currencyPipe.transform(Amount / 100, 'USD', 'symbol'),
    addGroupBrackets: true
  };

  public readonly DateTag: ModelTemplate<InvoiceOData> = {
    tag: this.#date.tag,
    items: (state): Observable<InvoiceOData[]> => {
      const term = state.leftCaretValue;

      this.paymentsService.filter = term || '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<InvoiceOData[]>;
      // }

      return scheduled(
        this.paymentsService$.pipe(
          map((res) => res.data),
          map((invoices) => {
            const cloneInvoices = cloneDeep(invoices).map((invoice) => ({ ...invoice, Date: this.datePipe.transform(invoice.Date) }));
            const filtered = cloneInvoices.filter((invoice) => invoice.Date && invoice.Date.includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#date.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, 'Date', 'Date');
          }),
          tap<InvoiceOData[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ Date }): string => this.datePipe.transform(Date, 'mediumDate'),
    addGroupBrackets: true
  };

  public readonly ExpireDateTag: ModelTemplate<InvoiceOData> = {
    tag: this.#expireDate.tag,
    items: (state): Observable<InvoiceOData[]> => {
      const term = state.leftCaretValue;

      this.paymentsService.filter = term || '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<InvoiceOData[]>;
      // }

      return scheduled(
        this.paymentsService$.pipe(
          map((res) => res.data),
          map((invoices) => {
            const cloneInvoices = cloneDeep(invoices).map((invoice) => ({
              ...invoice,
              ExpireDate: this.datePipe.transform(invoice.ExpireDate)
            }));
            const filtered = cloneInvoices.filter((invoice) => invoice.ExpireDate && invoice.ExpireDate.includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#expireDate.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, 'ExpireDate', 'Date');
          }),
          tap<InvoiceOData[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ ExpireDate }): string => this.datePipe.transform(ExpireDate, 'mediumDate'),
    addGroupBrackets: true
  };

  public readonly InvoiceIdTag: ModelTemplate<InvoiceOData> = {
    tag: this.#invoiceId.tag,
    items: (state): Observable<InvoiceOData[]> => {
      const term = state.leftCaretValue;

      this.paymentsService.filter = term || '';

      // if (this.cache[this.keyCache + term]) {
      //   return of(this.cache[this.keyCache + term]) as Observable<InvoiceOData[]>;
      // }

      return scheduled(
        this.paymentsService$.pipe(
          map((res) => res.data),
          map((invoices) => {
            const filtered = invoices.filter((invoice) => invoice.OrderId?.includes(term));
            const uniqueList = getUniqueListByProp(filtered, this.#invoiceId.prop).slice(0, 10);

            return this.sortOrderByPipe.transform(uniqueList, 'OrderId');
          }),
          tap<InvoiceOData[]>(this.writeToCache(this.keyCache + term))
        ),
        asapScheduler
      );
    },
    itemFormatter: ({ OrderId }): string => OrderId,
    addGroupBrackets: true
  };
}
